import { TrendingUp, TrendingDown, Minus, Home, Clock, Tag } from "lucide-react";
import { formatMarkdownToSafeHtml } from "@/lib/sanitize";
import { format } from "date-fns";

interface NeighborhoodDataPreviewProps {
  neighborhoodData: any;
  neighborhoodSummary?: string | null;
  neighborhoodDataUpdatedAt?: string | null;
  forceMobileLayout?: boolean;
}

export const NeighborhoodDataPreview = ({ neighborhoodData, neighborhoodSummary, neighborhoodDataUpdatedAt, forceMobileLayout }: NeighborhoodDataPreviewProps) => {
  if (!neighborhoodData) {
    return null;
  }

  const { metrics, analysis } = neighborhoodData;

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'Rising':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'Easing':
        return <TrendingDown className="h-5 w-5 text-blue-600" />;
      default:
        return <Minus className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getDemandColor = (rating: number) => {
    if (rating >= 7) return 'text-green-600';
    if (rating >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDataDate = (date: string | null | undefined) => {
    if (!date) return "Unknown";
    try {
      return format(new Date(date), "MMMM d, yyyy");
    } catch {
      return "Unknown";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold tracking-wider text-foreground mb-4">
          THE MARKET PULSE
        </h3>
      </div>

      {/* AI Summary */}
      {neighborhoodSummary && (
        <div className="mb-6">
          <div
            className="text-base text-foreground/90 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: formatMarkdownToSafeHtml(neighborhoodSummary)
            }}
          />
          {neighborhoodDataUpdatedAt && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              Data as of {formatDataDate(neighborhoodDataUpdatedAt)}
            </p>
          )}
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className={`grid ${forceMobileLayout ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'} gap-4`}>
        {/* Demand Rating */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Home className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase">Demand Rating</span>
          </div>
          <div className={`text-3xl font-bold ${getDemandColor(analysis.demandRating)}`}>
            {analysis.demandRating}/10
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {analysis.demandRating >= 7
              ? 'Strong buyer demand'
              : analysis.demandRating >= 5
              ? 'Moderate demand'
              : 'Softer market'}
          </p>
        </div>

        {/* Competition Trend */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground uppercase">Competition Trend</span>
          </div>
          <div className="flex items-center gap-2">
            {getTrendIcon(analysis.buyerTrend)}
            <span className="text-2xl font-bold text-foreground">
              {analysis.buyerTrend}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {analysis.buyerTrend === 'Rising'
              ? 'Increasing competition'
              : analysis.buyerTrend === 'Easing'
              ? 'Decreasing competition'
              : 'Balanced market'}
          </p>
        </div>

        {/* Market Velocity */}
        <div className="p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase">Avg Days to Sell</span>
          </div>
          <div className="text-3xl font-bold text-foreground">
            {metrics.soldDomMedian || 'N/A'}
            {metrics.soldDomMedian && <span className="text-base text-muted-foreground ml-1">days</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Recent sales velocity
          </p>
        </div>
      </div>

      {/* Market Activity Summary */}
      <div className="p-4 bg-card border border-border rounded-lg">
        <h4 className="text-sm font-semibold text-foreground mb-3">Market Activity</h4>
        <div className={`grid ${forceMobileLayout ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-4`}>
          <div>
            <div className="text-xs text-muted-foreground mb-1">New Listings</div>
            <div className="text-xl font-bold text-foreground">{metrics.newListings}</div>
            <div className="text-[10px] text-muted-foreground">Last 7 days</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Active</div>
            <div className="text-xl font-bold text-foreground">{metrics.activeListings}</div>
            <div className="text-[10px] text-muted-foreground">Currently for sale</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Pending</div>
            <div className="text-xl font-bold text-orange-600">{metrics.pendingListings}</div>
            <div className="text-[10px] text-muted-foreground">Under contract</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Sold</div>
            <div className="text-xl font-bold text-green-600">{metrics.soldListings}</div>
            <div className="text-[10px] text-muted-foreground">Last 90 days</div>
          </div>
        </div>
      </div>

      {/* Price Reductions & Inventory */}
      <div className={`grid ${forceMobileLayout ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
        <div className="p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase">Price Reductions</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">{metrics.priceReductions}</span>
            <span className="text-sm text-muted-foreground">listings ({metrics.priceReductionRate}%)</span>
          </div>
        </div>

        <div className="p-4 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Home className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase">Inventory Supply</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">
              {analysis.monthsOfInventory?.toFixed(1) || 'N/A'}
            </span>
            <span className="text-sm text-muted-foreground">months</span>
          </div>
        </div>
      </div>

      {/* Days on Market Comparison */}
      {metrics.activeDomMedian && metrics.soldDomMedian && (
        <div className="p-4 bg-card border border-border rounded-lg">
          <h4 className="text-sm font-semibold text-foreground mb-3">Days on Market Comparison</h4>
          <p className="text-xs text-muted-foreground mb-4">Active listings vs. recent sales</p>
          <div className={`grid ${forceMobileLayout ? 'grid-cols-2 gap-4' : 'grid-cols-2 md:grid-cols-2 gap-4 md:gap-8'}`}>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Active Listings</div>
              <div className="text-2xl md:text-4xl font-bold text-foreground">{metrics.activeDomMedian}</div>
              <div className="text-xs text-muted-foreground mt-1">days (median)</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-2">Recent Sales</div>
              <div className="text-2xl md:text-4xl font-bold text-green-600">{metrics.soldDomMedian}</div>
              <div className="text-xs text-muted-foreground mt-1">days (median)</div>
            </div>
          </div>
          {metrics.activeDomMedian > metrics.soldDomMedian * 1.4 && (
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-xs md:text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ Active listings are sitting longer than recent sales, indicating a potential market slowdown
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
